import { ReactComponent as Search } from '../../assets/icons/search.svg'
import copys from '../../utils/copys';
import IconLocation from '../../components/icons/IconLocationHome'
import { useLocationContext } from "../../hooks/useLocationContext"
import { useEffect, useState, useCallback  } from 'react';
import { Clipboard } from 'lucide-react';
import AddressPrompt from './AddressPrompt';
import { AddressData, LocationData } from '../../models/location';
import { TailSpin } from 'react-loader-spinner';
import { cn } from '../../utils/cn';
import { pushInteractionEvent } from '../../services/gtmService';

interface LocationSectionProps {
    isLoaded: boolean;
}

const LocationSection = ({ isLoaded }: LocationSectionProps) => {

    const [copiedValue, setCopiedValue] = useState('');
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [showInitialPrompts, setShowInitialPrompts] = useState(true);
    const [defaultSuggestions, setDefaultSuggestions] = useState<AddressData[]>()
    const { isLocationInputFocused, setIsLocationInputFocused, locationUserData, setLocationUserData, showFakeDataTour, neverShowTourAgain, setHasStartedTour, setShowFakeDataTour } = useLocationContext();

    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
    const [status, setStatus] = useState('');

    useEffect(() => {
        if (!isLoaded || !value.trim()) {
            setSuggestions([]);
            setStatus('');
            return;
        }

        const handler = setTimeout(async () => {
            try {
                const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places') as google.maps.PlacesLibrary;

                const request = {
                    input: value,
                    includedRegionCodes: ['mx'],
                    language: 'es-419'
                };

                const { suggestions: newSuggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
                setSuggestions(newSuggestions || []);
                setStatus(newSuggestions.length > 0 ? 'OK' : 'ZERO_RESULTS');
            } catch (error) {
                console.error("Error fetching autocomplete suggestions:", error);
                setStatus('ERROR');
            }
        }, 400);

        return () => {
            clearTimeout(handler);
        };
    }, [value, isLoaded]);

    const { getLocation } = useLocationContext();

    const handleFocus = () => {
        if (navigator.clipboard) (
            navigator.clipboard.readText().then(val => { if (val !== '') setCopiedValue(val) }).catch(e => console.log('error in reading clipboard', e))
        )
        setIsLocationInputFocused(true);
    }


    const handleButtonClick = async () => {
        pushInteractionEvent('interaction_home', 'geolocation_button');
        setLoadingLocation(true);
        await getLocation();
        setLoadingLocation(false)
    }
    
    const handleSelect = useCallback(async (suggestion: google.maps.places.AutocompleteSuggestion) => {
        const placePrediction = suggestion.placePrediction;
        if (!placePrediction) return;

        const mainText = placePrediction.mainText?.text || '';
        const fullAddress = placePrediction.text?.text;

        pushInteractionEvent('select_address', mainText);
        setValue(fullAddress);
        setSuggestions([]);
        setLoadingLocation(true);

        try {
            const place = await placePrediction.toPlace();
            await place.fetchFields({ fields: ['location'] });

            if (!place.location) {
                throw new Error("Location not found for the selected place.");
            }

            const lat = place.location.lat();
            const lng = place.location.lng();

            const formattedPrompts: AddressData[] = [{
                mainText: mainText,
                secondaryText: placePrediction.secondaryText?.text || '',
                fullAddress: fullAddress,
            }];

            setLocationUserData({
                locationType: 'manual',
                location: {
                    latitude: lat,
                    longitude: lng,
                    addressPrompts: formattedPrompts,
                    addressSelected: formattedPrompts[0]
                }
            });

            setIsLocationInputFocused(false);
            if (!neverShowTourAgain) {
                setShowFakeDataTour(true);
            }
            setHasStartedTour(true);
            setDefaultSuggestions(formattedPrompts);

        } catch (error) {
            console.error("Error getting place details:", error);
        } finally {
            setLoadingLocation(false);
        }
    }, [neverShowTourAgain, setHasStartedTour, setIsLocationInputFocused, setLocationUserData, setShowFakeDataTour]);

    const handleSelectForInitialPrompts = async (suggestion: AddressData) => {
        pushInteractionEvent('select_address', suggestion.mainText);
        setLoadingLocation(true);
        try {
            const { Geocoder } = await window.google.maps.importLibrary('geocoding') as google.maps.GeocodingLibrary;
            const geocoder = new Geocoder();
            const response = await geocoder.geocode({ address: suggestion.fullAddress });

            const location = response.results[0].geometry.location;
            const lat = location.lat();
            const lng = location.lng();

            const newLocationData: LocationData = { ...locationUserData.location! };
            newLocationData.addressSelected = suggestion;
            newLocationData.latitude = lat;
            newLocationData.longitude = lng;

            setLocationUserData({ ...locationUserData, location: newLocationData });
            setIsLocationInputFocused(false);
        } catch (error) {
            console.error("Error geocoding initial prompt:", error);
        } finally {
            setLoadingLocation(false);
        }
    };

    const renderSuggestions = () => {
        return suggestions.slice(0, 3).map((suggestion) => {
            const placePrediction = suggestion.placePrediction;
            if (!placePrediction) return null;

            return (
                <AddressPrompt
                    key={placePrediction.placeId}
                    title={placePrediction.mainText?.text || ''}
                    detail={placePrediction.secondaryText?.text || ''}
                    onClick={() => handleSelect(suggestion)}
                />
            );
        });
    }

    useEffect(() => {
        setShowInitialPrompts(false)
    }, [suggestions])

    useEffect(() => {
        if (isLocationInputFocused) {
            setHasStartedTour(false)
            if (locationUserData.location?.addressSelected) {
                setValue(locationUserData.location?.addressSelected.mainText)
                setDefaultSuggestions(locationUserData.location.addressPrompts);
                setShowInitialPrompts(true);
            }
        } else {
            setValue('');
            setSuggestions([]);
            setShowInitialPrompts(false);
        }
    }, [isLocationInputFocused]);

    useEffect(() => {
        if (locationUserData.locationType === 'gps') {
            setDefaultSuggestions(locationUserData.location?.addressPrompts);
            setShowInitialPrompts(false)
        }
    }, [locationUserData])


    return (
        <div className='flex flex-col gap-4 items-center w-full px-4'>
            {
                loadingLocation && (
                    <div className='w-screen h-screen z-50 bg-opacity-20 flex justify-center items-center'>
                        <TailSpin
                            visible={true}
                            height="80"
                            width="80"
                            color="#FFB100"
                            ariaLabel="tail-spin-loading"
                            radius="1"
                            strokeWidth={4}
                            wrapperStyle={{}}
                            wrapperClass=""
                        />
                    </div>
                )
            }

            <div className={cn('flex gap-4 w-full', isLocationInputFocused ? 'px-4' : 'justify-center')}>
                {
                    (!showFakeDataTour && locationUserData.locationType === 'none') || isLocationInputFocused
                        ?
                        (
                            <div className='bg-white py-3 px-4 flex gap-3 items-center rounded-2xl w-full' onClick={() => pushInteractionEvent('interaction_home', 'Ingresa una dirección o colonia...')}>
                                <Search />
                                <input id='inputAddress' disabled={!isLoaded} value={value} onChange={(e) => setValue(e.target.value)} onFocus={handleFocus} className='w-full font-roboto text-sm font-normal text-blue_text placeholder:text-[#5E6488]' type='text' placeholder={copys.inputLocationPlaceholder} />
                            </div>
                        )
                        :
                        (
                            <div className="flex w-full pl-0.5 gap-4 items-center">
                                <div id='textAddress' className="font-roboto text-sm text-blue_text font-semibold w-full text-center" onClick={() => setIsLocationInputFocused(true)}>
                                    {showFakeDataTour ? 'Av. Paseo de la Reforma 305' : locationUserData.location?.addressSelected.mainText}
                                </div>
                                <svg className='mx-0.5' xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none" onClick={() => setIsLocationInputFocused(true)}>
                                    <path d="M6.49945 8.9357L2.5957 5.0282L3.65321 3.9707L6.49945 6.8132L9.3457 3.9707L10.4032 5.0282L6.49945 8.9357Z" fill="#070561" />
                                </svg>
                            </div>
                        )

                }
                {
                    !isLocationInputFocused && (
                        <div id='buttonGeolocation' className="bg-[#100E9D] rounded-xl p-4 cursor-pointer flex justify-center items-center" onClick={handleButtonClick}>
                            <IconLocation size={16} />
                        </div>
                    )
                }
            </div>
            {
                isLocationInputFocused && (
                    <>
                        {
                            copiedValue !== '' && (
                                <div id='promptCopiedValue' className='flex justify-start items-center w-full px-6 py-5 gap-1 font-libre-franklin text-blue_text text-xs font-bold cursor-pointer' onClick={() => setValue(copiedValue)}>
                                    <Clipboard size={24} color='#100E9D' />
                                    {copys.promptPasteCopiedAddress}
                                </div>
                            )
                        }
                        {
                            showInitialPrompts && value !== '' && defaultSuggestions && defaultSuggestions?.length > 0 && defaultSuggestions?.map(suggestion => (
                                <AddressPrompt key={suggestion.secondaryText} title={suggestion.mainText} detail={suggestion.secondaryText} onClick={() => handleSelectForInitialPrompts(suggestion)} />
                            ))
                        }
                        {
                            status === "OK" && !showInitialPrompts && renderSuggestions()
                        }
                        <div className='bg-[#617482] h-[0.5px] w-full' />
                        <div className='text-[#777] font-libre-franklin text-xs'>
                            Powered by <a className='font-semibold' href="https://www.google.com/maps" target="_blank" rel="noreferrer">Google</a>
                        </div>
                    </>
                )
            }
        </div>
    )
}

export default LocationSection
